import {
  Button,
  Col,
  Descriptions,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Row,
  Select,
  Switch,
  Table,
  Tag,
} from 'antd';
import {PlusOutlined, InfoCircleOutlined, EditTwoTone, BankOutlined} from '@ant-design/icons';

import {ColumnsType, TablePaginationConfig} from 'antd/lib/table';
import {useEffect, useState} from 'react';
import {useAppDispatch, useAppSelector} from '../../../../common/hooks';
import {
  stationsList,
  addStation,
  updateStation,
  pageNumberChange,
  pageSizeChange,
  searchBy,
  stationsStatusFilter,
  sortBy,
} from './stationsSlice';
import type {ADD_STATION, STATION_DATA, UPDATE_STATION} from './stations.type';
import {formatDate} from '../../../../utils/formatDate';
import checkFormUpdate from '../../../../utils/checkFormUpdate';
import {
  billingStrategy,
  sortbyFilter,
  statusFilter,
  statusFilterOptions,
} from '../../../../common/constants';

const Stations = () => {
  const dispatch = useAppDispatch();
  const stations = useAppSelector((state) => state.stations);
  const permission = useAppSelector((state) => state.config.config);
  const [billing, setBilling] = useState('');
  const [isStationModalOpen, setIsStationModalOpen] = useState(false);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [isUpdateModal, setIsUpdateModal] = useState(false);
  const [updateStationForm] = Form.useForm();

  const [modalData, setModalData] = useState<STATION_DATA>({} as STATION_DATA);

  const handleUpdateStation = (values: {
    name: string;
    address: string;
    lat: number;
    long: number;
    is_active: boolean;
    billing_strategy?: string;
    flat_price?: number;
    energy_unit_price?: number;
  }) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const data: any = {
      name: values.name,
      address: values.address,
      is_active: values.is_active,
    };
    data.location = {x: values.lat, y: values.long};
    if (values.billing_strategy !== undefined) {
      data.extra_details = {
        billing_config: {
          billingStrategy: values.billing_strategy,
          flatPrice: values.flat_price,
          energyUnitPrice: values.energy_unit_price,
        },
      };
    }
    const payload: UPDATE_STATION = checkFormUpdate(data, modalData);

    if (Object.keys(payload).length > 0) {
      dispatch(updateStation({payload: payload, id: modalData.id}))
        .unwrap()
        .then(() => {
          handleStationModalClose();
          dispatch(
            stationsList({
              page_size: stations.stations.page_size,
              page_number: stations.stations.page_number,
              sort_by: stations.stations.sort_by,
              ascending: stations.stations.ascending,
              search: stations.stations.search,
            }),
          );
        });
    } else {
      message.warning('No field changed');
    }
  };

  const handleAddStation = (values: {
    name: string;
    address: string;
    lat: number;
    long: number;
    asset_id: string;
    billing_strategy?: string;
    flat_price?: number;
    energy_unit_price?: number;
  }) => {
    const payload = {} as ADD_STATION;
    payload.location = {x: values.lat, y: values.long};
    payload.name = values.name;
    payload.address = values.address;
    payload.asset_id = values.asset_id;
    if (values.billing_strategy !== undefined) {
      payload.extra_details = {
        billing_config: {
          billingStrategy: values.billing_strategy,
          flatPrice:
            values.billing_strategy === billingStrategy.SOC_BASED ? null : values.flat_price,
          energyUnitPrice:
            values.billing_strategy === billingStrategy.FLAT ? null : values.energy_unit_price,
        },
      };
    }

    dispatch(addStation(payload))
      .unwrap()
      .then(() => {
        handleStationModalClose();
        dispatch(
          stationsList({
            page_size: stations.stations.page_size,
            page_number: stations.stations.page_number,
            sort_by: stations.stations.sort_by,
            ascending: stations.stations.ascending,
            search: stations.stations.search,
          }),
        );
      });
  };
  const handleStationModalClose = () => {
    setIsStationModalOpen(false);
    updateStationForm.resetFields();
  };

  useEffect(() => {
    //
    dispatch(
      stationsList({
        page_size: stations.stations.page_size,
        page_number: stations.stations.page_number,
        sort_by: stations.stations.sort_by,
        ascending: stations.stations.ascending,
        search: stations.stations.search,
        is_active: stations.stations.is_active,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    stations.stations.page_number,
    stations.stations.page_size,
    stations.stations.search,
    stations.stations.is_active,
    stations.stations.sort_by,
    stations.stations.ascending,
  ]);

  const columns: ColumnsType<STATION_DATA> = [
    {
      title: 'S. No.',
      width: 70,
      render: (test, record, index) => {
        return <>{(stations.stations.page_number - 1) * stations.stations.page_size + index + 1}</>;
      },
    },
    {
      title: 'Station Name',
      dataIndex: 'name',
      sorter: true,
    },
    {
      title: ' Address',
      dataIndex: 'address',
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      render: (_, record) =>
        record.is_active ? <Tag color='green'>Active</Tag> : <Tag color='red'>Inactive</Tag>,
    },
    {
      title: 'Actions',
      key: 'operation',
      render: (_, record) => {
        return (
          <>
            <InfoCircleOutlined
              style={{cursor: 'pointer', fontSize: '16px', color: 'green', marginInline: '10px'}}
              onClick={() => {
                setModalData(record);
                setIsInfoModalOpen(true);
              }}
            />
            {permission.permissions.update_station && (
              <EditTwoTone
                style={{cursor: 'pointer', fontSize: '16px', color: 'blue', marginInline: '10px'}}
                onClick={() => {
                  setModalData(record);
                  setIsUpdateModal(true);
                  updateStationForm.setFieldsValue({
                    name: record.name,
                    address: record.address,
                    lat: record.location.x,
                    long: record.location.y,
                    is_active: record.is_active,
                    billing_strategy:
                      record.extra_details !== null
                        ? record.extra_details.billing_config.billingStrategy
                        : undefined,
                    flat_price:
                      record.extra_details !== null
                        ? record.extra_details.billing_config.flatPrice
                        : undefined,
                    energy_unit_price:
                      record.extra_details !== null
                        ? record.extra_details.billing_config.energyUnitPrice
                        : undefined,
                  });
                  record.extra_details !== null
                    ? setBilling(record.extra_details.billing_config.billingStrategy)
                    : '';
                  setIsStationModalOpen(true);
                }}
              />
            )}{' '}
          </>
        );
      },
    },
  ];

  const handleSearch = (e: string) => {
    if (e.length > 3) {
      dispatch(searchBy(e));
    } else if (e.length === 0) {
      dispatch(searchBy(e));
    }
  };

  return (
    <div style={{paddingInline: '20px'}}>
      <div
        style={{
          margin: '10px 0px 10px 0px',
          width: '100%',
          display: 'grid',
          gridTemplateColumns: '350px 1fr 1fr',
        }}
      >
        <Input.Search
          placeholder='Search'
          defaultValue={stations.stations.search}
          style={{borderRadius: '10px'}}
          allowClear
          onSearch={(e) => handleSearch(e)}
        />
        <Select
          style={{width: '100px', marginLeft: '5px'}}
          value={
            stations.stations.is_active === undefined
              ? statusFilter.ALL
              : stations.stations.is_active === true
              ? statusFilter.ACTIVE
              : statusFilter.INACTIVE
          }
          options={statusFilterOptions}
          onChange={(value) => {
            if (value === statusFilter.ACTIVE) {
              dispatch(stationsStatusFilter(true));
            } else if (value === statusFilter.ALL) {
              dispatch(stationsStatusFilter(undefined));
            } else {
              dispatch(stationsStatusFilter(false));
            }
          }}
          showArrow
        />
        <div style={{width: '100%', textAlign: 'end'}}>
          {permission.permissions.add_station ? (
            <Button
              type='primary'
              icon={<PlusOutlined />}
              style={{borderRadius: '5px', marginInline: '5px'}}
              onClick={() => {
                setIsUpdateModal(false);
                setIsStationModalOpen(true);
              }}
            >
              Add Station
            </Button>
          ) : null}
        </div>
      </div>
      <Table
        rowKey='id'
        columns={columns}
        pagination={
          {
            total: stations.stations.totalDataCount,
            current: stations.stations.page_number,
            pageSize: stations.stations.page_size,
            showSizeChanger: true,
            // onChange: (pageNumber, pageSize) => {
            //   if (pageSize !== stations.stations.page_size) {
            //     dispatch(pageSizeChange({pageNumber, pageSize}));
            //   } else {
            //     dispatch(pageNumberChange(pageNumber));
            //   }
            // },
          } as TablePaginationConfig
        }
        loading={stations.isLoading}
        size='middle'
        dataSource={stations.stations.pageData}
        scroll={{x: undefined, y: '69vh'}}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onChange={(pagination, _, {order, field}: any) => {
          if (pagination.pageSize && pagination.pageSize !== stations.stations.page_size) {
            dispatch(pageSizeChange(pagination.pageSize));
          }
          if (pagination.current && pagination.current !== stations.stations.page_number) {
            dispatch(pageNumberChange(pagination.current));
          }
          if (field) {
            if (order === sortbyFilter.DESCEND) {
              dispatch(sortBy({ascending: false, sortBy: field}));
            } else {
              dispatch(sortBy({ascending: true, sortBy: field}));
            }
          }
        }}
      />
      {isInfoModalOpen && (
        <Modal
          title={`${modalData?.name}`}
          open={isInfoModalOpen}
          onCancel={() => {
            setIsInfoModalOpen(false);
          }}
          footer={null}
          width={800}
        >
          <Descriptions bordered column={2} labelStyle={{fontWeight: 'bolder'}}>
            <Descriptions.Item label='Station Name'>{modalData?.name}</Descriptions.Item>
            <Descriptions.Item label='Station Id'>{modalData?.id}</Descriptions.Item>

            <Descriptions.Item label='Is Active'>
              {modalData?.is_active ? (
                <Tag color='success'>Active</Tag>
              ) : (
                <Tag color='error'> Inactive</Tag>
              )}
            </Descriptions.Item>
            <Descriptions.Item label='Created By'>{modalData?.created_by}</Descriptions.Item>
            <Descriptions.Item label='Created At'>
              {formatDate(modalData?.created_at)}
            </Descriptions.Item>
            <Descriptions.Item label='Updated By'>{modalData?.updated_by}</Descriptions.Item>
            <Descriptions.Item label='Updated At'>
              {formatDate(modalData?.updated_at)}
            </Descriptions.Item>
            <Descriptions.Item label='Address'>{modalData?.address}</Descriptions.Item>
            {modalData.extra_details !== null && (
              <>
                <Descriptions.Item label='Billing Strategy'>
                  {modalData.extra_details.billing_config.billingStrategy}
                </Descriptions.Item>
                {modalData.extra_details.billing_config.billingStrategy !==
                  billingStrategy.SOC_BASED && (
                  <Descriptions.Item label='Flat  Price'>
                    {modalData.extra_details.billing_config.flatPrice}
                  </Descriptions.Item>
                )}
                {modalData.extra_details.billing_config.billingStrategy !==
                  billingStrategy.FLAT && (
                  <Descriptions.Item label='Energy Unit Price '>
                    {modalData.extra_details.billing_config.energyUnitPrice}
                  </Descriptions.Item>
                )}
              </>
            )}
          </Descriptions>
        </Modal>
      )}
      <Modal
        title={isUpdateModal ? 'Update Station' : 'Add  Station'}
        open={isStationModalOpen}
        maskClosable={false}
        onCancel={() => {
          handleStationModalClose();
        }}
        okText={isUpdateModal ? 'Update Station' : 'Add  Station'}
        onOk={() => {
          updateStationForm
            .validateFields()
            .then((values) => {
              //updateStationForm.resetFields();
              {
                isUpdateModal ? handleUpdateStation(values) : handleAddStation(values);
              }
            })
            .catch((info) => {
              console.error('Validate Failed:', info);
            });
        }}
      >
        <Row>
          <Col span={24}>
            <Form name='stationForm' form={updateStationForm}>
              <Form.Item
                name='name'
                rules={[
                  {required: true, message: 'Please fill your full Name '},
                  {min: 4, message: 'Minimum length is 4 characters'},
                ]}
                label=' Station Name'
                labelCol={{span: 24}}
              >
                <Input
                  autoComplete='off'
                  prefix={<BankOutlined className='site-form-item-icon' />}
                  placeholder='Full Name'
                />
              </Form.Item>
              <Form.Item
                name='address'
                rules={[
                  {required: true, message: 'Please fill your Address '},
                  {min: 20, message: 'Minimum length is 20 characters'},
                  {max: 100, message: 'Maximum length is 100 characters'},
                ]}
                label='Address'
                labelCol={{span: 24}}
              >
                <Input.TextArea
                  autoComplete='off'
                  placeholder='Address'
                  autoSize={{maxRows: 4, minRows: 2}}
                  showCount
                  allowClear
                />
              </Form.Item>
              <Row>
                <Col span={11}>
                  <Form.Item
                    name='lat'
                    rules={[{required: true, message: 'Please fill your full Name '}]}
                    label='Latitude'
                    labelCol={{span: 24}}
                  >
                    <InputNumber
                      style={{width: '100%'}}
                      autoComplete='off'
                      placeholder='Latitude'
                    />
                  </Form.Item>
                </Col>
                <Col span={11} offset={2}>
                  <Form.Item
                    name='long'
                    rules={[{required: true, message: 'Please fill your full Name '}]}
                    label='Longitude'
                    labelCol={{span: 24}}
                  >
                    <InputNumber
                      style={{width: '100%'}}
                      autoComplete='off'
                      placeholder='Longitude'
                    />
                  </Form.Item>
                </Col>
              </Row>
              {!isUpdateModal && (
                <Form.Item
                  name='asset_id'
                  rules={[{required: true, message: 'Please fill asset Id '}]}
                  label=' Asset Id'
                  labelCol={{span: 24}}
                >
                  <Input autoComplete='off' placeholder='Asset Id' />
                </Form.Item>
              )}

              {isUpdateModal && (
                <Form.Item name='is_active' label='Is Active' valuePropName='checked'>
                  <Switch checkedChildren='Active' unCheckedChildren='Inactive' />
                </Form.Item>
              )}
              {permission.config.billing_config.allowStationBilling && (
                <>
                  <Row>
                    <Col span={24}>
                      <Form.Item
                        name='billing_strategy'
                        label='Billing Strategy'
                        labelCol={{span: 24}}
                      >
                        <Select
                          showSearch
                          placeholder='Select Billing Strategy'
                          defaultActiveFirstOption={false}
                          showArrow={true}
                          allowClear
                          filterOption={false}
                          onChange={(e) => {
                            setBilling(e);
                          }}
                        >
                          {permission.config.billing_strategy_options.map((x, i) => (
                            <Select.Option key={i} value={x.value}>
                              {x.label}
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row>
                    {billing === billingStrategy.FLAT ||
                    billing === billingStrategy.FLAT_EXTENDED ? (
                      <Col span={11}>
                        <Form.Item
                          name='flat_price'
                          rules={[
                            {
                              pattern: new RegExp('^[0-9]'),
                              required:
                                billing === billingStrategy.FLAT ||
                                billing === billingStrategy.FLAT_EXTENDED
                                  ? true
                                  : false,
                              message: 'Enter only numeric value',
                            },
                          ]}
                          label='Flat Price'
                          labelCol={{span: 24}}
                        >
                          <InputNumber style={{width: '100%'}} autoComplete='off' />
                        </Form.Item>
                      </Col>
                    ) : null}
                    {billing === billingStrategy.SOC_BASED ||
                    billing === billingStrategy.FLAT_EXTENDED ? (
                      <Col span={11} offset={billing === billingStrategy.SOC_BASED ? 0 : 2}>
                        <Form.Item
                          name='energy_unit_price'
                          rules={[
                            {
                              pattern: new RegExp('^[0-9]'),
                              required:
                                billing === billingStrategy.SOC_BASED ||
                                billing === billingStrategy.FLAT_EXTENDED
                                  ? true
                                  : false,

                              message: 'Enter only numeric value',
                            },
                          ]}
                          label='Energy Unit Price'
                          labelCol={{span: 24}}
                        >
                          <InputNumber style={{width: '100%'}} autoComplete='off' />
                        </Form.Item>
                      </Col>
                    ) : null}
                  </Row>
                </>
              )}
            </Form>
          </Col>
        </Row>
      </Modal>
    </div>
  );
};

export default Stations;
